import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, MenuItem, Select, Tab, Tabs } from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import ProgressBar from "../../UI/ProgressBar";
import { useAlert } from "../../../hooks/useAlert";
import {
  useGetPromotionEmailTemplateQuery,
  useUpdatePromotionEmailTemplateMutation,
  useUploadPromotionEmailAssetMutation,
} from "../../../redux-toolkit/features/promotions/promotionsApiSlice";
import { INVITE_COPY_VARIABLES } from "../../../constants/promotionEmailVariables";
import {
  DESIGN_COLOR_FIELDS,
  EDITOR_PREVIEW_DATA,
  EMAIL_TEMPLATE_LIMITS,
  EVENT_NAME_FONTS,
  PAGE_FONTS,
  compileInviteEmail,
  defaultInviteDesign,
  interpolateInvitePreview,
  mergeInviteDesign,
} from "./promotionInviteDesign";
import { VariableTextField } from "./VariableTextField";
import { HexColorPicker } from "./HexColorPicker";
import "./Promotions.css";
import "./EmailTemplates.css";

const TABS = [
  { value: "configs", label: "Email Configs" },
  { value: "content", label: "Content" },
  { value: "design", label: "Design" },
];

const assetUrlFromUpload = (payload) =>
  payload?.data?.location ||
  payload?.data?.url ||
  payload?.location ||
  payload?.url ||
  "";

const Field = ({
  label,
  required,
  value,
  onChange,
  max,
  disabled,
  placeholder,
}) => (
  <label className="email-design-field">
    <span>
      {label}
      {required ? <em>*</em> : null}
    </span>
    <input
      type="text"
      value={value}
      disabled={disabled}
      maxLength={max}
      placeholder={placeholder}
      onChange={(e) => onChange?.(e.target.value)}
    />
    {max ? (
      <em>
        Character limit: {String(value || "").length}/{max}
      </em>
    ) : null}
  </label>
);

const AssetBox = ({
  label,
  required,
  src,
  onUpload,
  onClear,
  uploading,
  wide,
}) => {
  const [dragOver, setDragOver] = useState(false);

  const takeFile = (file) => {
    if (!file || uploading) return;
    onUpload(file);
  };

  return (
    <div
      className={`email-design-asset${wide ? " email-design-asset--wide" : ""}`}
    >
      <span className="email-design-asset__label">
        {label}
        {required ? <em>*</em> : null}
      </span>
      <div
        className={`email-design-asset__box${
          dragOver ? " is-dragover" : ""
        }${src ? " has-preview" : ""}`}
        onDragEnter={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (!src) setDragOver(true);
        }}
        onDragOver={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (!src) setDragOver(true);
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setDragOver(false);
        }}
        onDrop={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setDragOver(false);
          const file = e.dataTransfer.files?.[0];
          takeFile(file);
        }}
      >
        {src ? (
          <>
            <img src={src} alt={label} />
            <button
              type="button"
              className="email-design-asset__clear"
              aria-label={`Remove ${label}`}
              onClick={(e) => {
                e.stopPropagation();
                onClear();
              }}
            >
              <CloseIcon sx={{ fontSize: 14 }} />
            </button>
          </>
        ) : (
          <div className="email-design-asset__empty">
            <UploadFileOutlinedIcon sx={{ fontSize: 70 }} />
            <p>
              Choose an image file{" "}
              <span>or drag it here.</span>
              <span>Maximum file size 1MB</span>
            </p>
            <input
              type="file"
              accept="image/*"
              className="email-design-asset__dropzone"
              disabled={uploading}
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = "";
                takeFile(file);
              }}
            />
          </div>
        )}
        {uploading ? (
          <span className="email-design-asset__uploading">Uploading…</span>
        ) : null}
      </div>
    </div>
  );
};

export const EmailTemplateEditor = () => {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const { displayAlert } = useAlert();
  const [tab, setTab] = useState("configs");
  const [designPanel, setDesignPanel] = useState("main");

  const { data, isLoading, error } =
    useGetPromotionEmailTemplateQuery(templateId, {
      skip: !templateId,
      refetchOnMountOrArgChange: true,
    });
  const [saveTemplate, { isLoading: isSaving }] =
    useUpdatePromotionEmailTemplateMutation();
  const [uploadAsset, { isLoading: isUploading }] =
    useUploadPromotionEmailAssetMutation();

  const template = data?.data?.template;
  const bookName = template?.book?.name || template?.templateName || "Promotion";
  const senderEmail = template?.senderEmail || "Quest.Servers@Qgroup.Se";
  const designKey = template ? `${template._id}:${template.version}` : null;

  const savedDesign = useMemo(() => {
    if (!template) return defaultInviteDesign(bookName);
    return mergeInviteDesign(
      {
        ...(template.design || {}),
        bookLogoUrl: template.design?.bookLogoUrl || template.logoUrl || "",
      },
      bookName
    );
  }, [
    template?._id,
    template?.version,
    template?.design,
    template?.logoUrl,
    bookName,
  ]);

  const [draftDesign, setDraftDesign] = useState(null);
  const [draftKey, setDraftKey] = useState(null);

  useLayoutEffect(() => {
    if (!designKey) return;
    setDraftDesign(savedDesign);
    setDraftKey(designKey);
    // Only rehydrate when the saved template identity/version changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [designKey]);

  const design =
    draftKey === designKey && draftDesign ? draftDesign : savedDesign;
  const setDesign = (updater) => {
    setDraftKey(designKey);
    setDraftDesign((prev) => {
      const current =
        draftKey === designKey && prev ? prev : savedDesign;
      return typeof updater === "function" ? updater(current) : updater;
    });
  };

  const compiled = useMemo(
    () => compileInviteEmail(design, bookName),
    [design, bookName]
  );
  const previewHtml = useMemo(
    () =>
      interpolateInvitePreview(compiled.html, {
        ...EDITOR_PREVIEW_DATA,
        bookName,
      }),
    [compiled.html, bookName]
  );

  const patch = (partial) =>
    setDesign((prev) => mergeInviteDesign({ ...prev, ...partial }, bookName));
  const patchColor = (key, value) =>
    setDesign((prev) =>
      mergeInviteDesign(
        { ...prev, colors: { ...prev.colors, [key]: value } },
        bookName
      )
    );

  const goBack = () => navigate("/super-admin/promotions/email-templates");

  const persist = async () => {
    const result = await saveTemplate({
      templateId,
      design,
    }).unwrap();
    displayAlert({
      open: true,
      message: result?.message || "Email template saved",
      type: result?.data?.provision?.action === "failed" ? "warning" : "success",
      timeout: "2500",
    });
    return result;
  };

  const handleSaveNext = async () => {
    if (tab === "configs" && !String(design.subject || "").trim()) {
      displayAlert({
        open: true,
        message: "Subject line is required",
        type: "error",
        timeout: "2500",
      });
      return;
    }
    if (
      tab === "content" &&
      (!design.headline.trim() || !design.ctaSubject.trim() || !design.cta.trim())
    ) {
      displayAlert({
        open: true,
        message: "Headline and CTA fields are required",
        type: "error",
        timeout: "2500",
      });
      return;
    }
    try {
      await persist();
      if (tab === "configs") setTab("content");
      else if (tab === "content") setTab("design");
      else if (designPanel !== "main") setDesignPanel("main");
    } catch (err) {
      displayAlert({
        open: true,
        message:
          err?.data?.message || err?.message || "Unable to save email template",
        type: "error",
        timeout: "3000",
      });
    }
  };

  const handleResetView = () => {
    const defaults = defaultInviteDesign(bookName);
    if (tab === "configs") patch({ subject: defaults.subject });
    else if (tab === "content") {
      patch({
        headline: defaults.headline,
        ctaSubject: defaults.ctaSubject,
        cta: defaults.cta,
      });
    } else if (designPanel === "colour") {
      patch({ colors: defaults.colors });
    } else if (designPanel === "text") {
      patch({
        pageFont: defaults.pageFont,
        eventNameFont: defaults.eventNameFont,
      });
    } else {
      patch({
        bookLogoUrl: "",
        companyLogoUrl: "",
        backgroundImageUrl: "",
      });
    }
  };

  const handleUpload = async (key, file) => {
    if (file?.size > 1 * 1024 * 1024) {
      displayAlert({
        open: true,
        message: "Choose Image smaller than 1MB",
        type: "error",
        timeout: "3000",
      });
      return;
    }
    try {
      const result = await uploadAsset(file).unwrap();
      const url = assetUrlFromUpload(result);
      if (!url) throw new Error("Upload did not return a file URL");
      patch({ [key]: url });
    } catch (err) {
      displayAlert({
        open: true,
        message: err?.data?.message || err?.message || "Unable to upload image",
        type: "error",
        timeout: "3000",
      });
    }
  };

  if (isLoading) {
    return (
      <div className="promotions-page container">
        <ProgressBar />
      </div>
    );
  }

  if (error || !template) {
    return (
      <div className="promotions-page container">
        <p className="promotions-table__empty">Email template not found</p>
        <Button onClick={goBack}>Back to Email Template</Button>
      </div>
    );
  }

  const showDesignSubnav = tab === "design" && designPanel !== "main";

  return (
    <div className="promotions-page email-template-studio container">
      <nav className="email-template-studio__crumb">
        <button type="button" onClick={goBack}>
          Email Template
        </button>
        <span>›</span>
        <strong>{bookName}</strong>
      </nav>

      <div className="email-template-studio__grid">
        <section className="email-template-studio__card">
          <Tabs
            value={tab}
            onChange={(_e, value) => {
              setTab(value);
              setDesignPanel("main");
            }}
            className="promotions-page__tabs"
          >
            {TABS.map((item) => (
              <Tab key={item.value} label={item.label} value={item.value} />
            ))}
          </Tabs>

          <div className="email-template-studio__body">
            {showDesignSubnav && (
              <button
                type="button"
                className="email-template-studio__back"
                onClick={() => setDesignPanel("main")}
              >
                ← Back
              </button>
            )}

            {tab === "configs" && (
              <div className="email-template-studio__fields">
                <Field
                  label="Sender Email"
                  required
                  value={senderEmail}
                  disabled
                />
                <VariableTextField
                  label="Subject Line"
                  required
                  value={design.subject}
                  max={EMAIL_TEMPLATE_LIMITS.subject}
                  variables={INVITE_COPY_VARIABLES}
                  onChange={(subject) => patch({ subject })}
                />
              </div>
            )}

            {tab === "content" && (
              <div className="email-template-studio__fields">
                <Field label="Book Name" required value={bookName} disabled />
                <VariableTextField
                  label="Headline"
                  required
                  value={design.headline}
                  max={EMAIL_TEMPLATE_LIMITS.headline}
                  variables={INVITE_COPY_VARIABLES}
                  onChange={(headline) => patch({ headline })}
                />
                <VariableTextField
                  label="CTA Subject Line"
                  required
                  value={design.ctaSubject}
                  max={EMAIL_TEMPLATE_LIMITS.ctaSubject}
                  variables={INVITE_COPY_VARIABLES}
                  onChange={(ctaSubject) => patch({ ctaSubject })}
                />
                <Field
                  label="CTA"
                  required
                  value={design.cta}
                  max={EMAIL_TEMPLATE_LIMITS.cta}
                  onChange={(cta) => patch({ cta })}
                />
              </div>
            )}

            {tab === "design" && designPanel === "main" && (
              <div className="email-template-studio__fields">
                <label className="email-design-field">
                  <span>
                    Text <em>*</em>
                  </span>
                  <button
                    type="button"
                    className="email-design-nav"
                    onClick={() => setDesignPanel("text")}
                  >
                    <span>Text</span>
                    <ChevronRightIcon />
                  </button>
                </label>
                <label className="email-design-field">
                  <span>
                    Colour <em>*</em>
                  </span>
                  <button
                    type="button"
                    className="email-design-nav"
                    onClick={() => setDesignPanel("colour")}
                  >
                    <span>Colour</span>
                    <ChevronRightIcon />
                  </button>
                </label>
                <div className="email-design-assets">
                  <AssetBox
                    label="Book Logo"
                    required
                    src={design.bookLogoUrl}
                    uploading={isUploading}
                    onUpload={(file) => handleUpload("bookLogoUrl", file)}
                    onClear={() => patch({ bookLogoUrl: "" })}
                  />
                  <AssetBox
                    label="Company Logo"
                    required
                    src={design.companyLogoUrl}
                    uploading={isUploading}
                    onUpload={(file) => handleUpload("companyLogoUrl", file)}
                    onClear={() => patch({ companyLogoUrl: "" })}
                  />
                  <AssetBox
                    wide
                    label="Background Image"
                    required
                    src={design.backgroundImageUrl}
                    uploading={isUploading}
                    onUpload={(file) => handleUpload("backgroundImageUrl", file)}
                    onClear={() => patch({ backgroundImageUrl: "" })}
                  />
                </div>
              </div>
            )}

            {tab === "design" && designPanel === "colour" && (
              <div className="email-template-studio__fields">
                {DESIGN_COLOR_FIELDS.map((group) => (
                  <div key={group.group} className="email-design-color-group">
                    <h3>{group.group}</h3>
                    {group.items.map((item) => (
                      <HexColorPicker
                        key={item.key}
                        label={item.label}
                        value={design.colors[item.key]}
                        onChange={(value) => patchColor(item.key, value)}
                      />
                    ))}
                  </div>
                ))}
              </div>
            )}

            {tab === "design" && designPanel === "text" && (
              <div className="email-template-studio__fields">
                <label className="email-design-field">
                  <span>
                    Page Font <em>*</em>
                  </span>
                  <Select
                    size="small"
                    fullWidth
                    displayEmpty
                    IconComponent={ChevronRightIcon}
                    value={design.pageFont}
                    onChange={(e) => patch({ pageFont: e.target.value })}
                  >
                    {PAGE_FONTS.map((font) => (
                      <MenuItem key={font.id} value={font.id}>
                        {font.label}
                      </MenuItem>
                    ))}
                  </Select>
                </label>
                <label className="email-design-field">
                  <span>
                    Event Name Font <em>*</em>
                  </span>
                  <Select
                    size="small"
                    fullWidth
                    displayEmpty
                    IconComponent={ChevronRightIcon}
                    value={design.eventNameFont}
                    onChange={(e) => patch({ eventNameFont: e.target.value })}
                  >
                    {EVENT_NAME_FONTS.map((font) => (
                      <MenuItem key={font.id} value={font.id}>
                        {font.label}
                      </MenuItem>
                    ))}
                  </Select>
                </label>
              </div>
            )}
          </div>

          <div className="email-template-studio__footer">
            {tab === "content" ? (
              <p className="email-template-studio__note">
                Note: Other details such as Participant Name, Event details,
                Host Name will be auto filled based on the Promotion creation.
              </p>
            ) : null}
            <div className="email-template-studio__footer-actions">
              <Button
                variant="outlined"
                className="email-template-studio__reset"
                onClick={handleResetView}
              >
                Reset
              </Button>
              <Button
                variant="contained"
                disableElevation
                className="email-template-studio__save"
                onClick={handleSaveNext}
                disabled={isSaving}
                sx={{
                  backgroundColor: "#3361cf",
                  color: "#fff",
                  "&:hover": { backgroundColor: "#1e4ab6" },
                  "&.Mui-disabled": {
                    backgroundColor: "#8a94a6",
                    color: "#fff",
                  },
                }}
              >
                {isSaving ? "Saving…" : "Save & Next"}
              </Button>
            </div>
          </div>
        </section>

        <aside className="email-template-studio__preview">
          <header>Preview</header>
          <div className="email-template-studio__preview-frame">
            <iframe title="Email template preview" sandbox="" srcDoc={previewHtml} />
          </div>
        </aside>
      </div>
    </div>
  );
};
